import { useCallback } from 'react'
import { atom, useAtomValue, useSetAtom } from 'jotai'

import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { IMPORT_STEPS, ImportItem, ImportProcessingState, ImportState } from '@/utils/grid-import'

const defaultState: ImportState = {
  step: null,
  fileType: null,
  fileName: '',
  fileExtension: '',
  type: 'import',
  activeSheet: 0,
  items: [],
  state: {},
}

// Atom for storing the import state
const importStateAtom = atom<ImportState>(defaultState)

/**
 * Custom hook for managing the import data process
 */
export const useGridImportData = () => {
  const { tables } = useDbClientContext()

  const importState = useAtomValue(importStateAtom)
  const setImportState = useSetAtom(importStateAtom)

  const startImport = useCallback(
    (dbId: string, fileType: 'csv' | 'excel' | null, type: 'import' | 'append' = 'import') => {
      setImportState((prevState) => ({ ...prevState, dbId: dbId, step: IMPORT_STEPS.SELECT_FILE, fileType, type }))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  /**
   * Moves to the next step in the import process
   */
  const nextStep = useCallback(() => {
    setImportState((prevState) => {
      switch (prevState.step) {
        case IMPORT_STEPS.SELECT_FILE:
          return { ...prevState, step: IMPORT_STEPS.PREVIEW_DATA }
        case IMPORT_STEPS.PREVIEW_DATA:
          return { ...prevState, step: IMPORT_STEPS.PROCESS_DATA }
        case IMPORT_STEPS.PROCESS_DATA:
          return { ...prevState, step: IMPORT_STEPS.COMPLETE }
        default:
          return prevState
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Moves to the previous step in the import process
   */
  const prevStep = useCallback(() => {
    setImportState((prevState) => {
      switch (prevState.step) {
        case IMPORT_STEPS.PREVIEW_DATA:
          return { ...prevState, step: IMPORT_STEPS.SELECT_FILE }
        case IMPORT_STEPS.PROCESS_DATA:
          return { ...prevState, step: IMPORT_STEPS.PREVIEW_DATA }
        case IMPORT_STEPS.COMPLETE:
          return { ...prevState, step: IMPORT_STEPS.PROCESS_DATA }
        default:
          return prevState
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Resets the import state
   */
  const resetImport = useCallback(() => {
    setImportState(defaultState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Updates the processing state for a specific import step
   */
  const updateProcessingState = useCallback(
    (step: string, state: ImportProcessingState) => {
      setImportState((prevState) => {
        return {
          ...prevState,
          state: {
            ...prevState.state,
            [step]: state,
          },
        }
      })
    },
    [setImportState]
  )

  const addImportItem = useCallback(
    (items: ImportItem[]) => {
      setImportState((prevState) => ({
        ...prevState,
        items: [...items],
      }))
    },
    [setImportState]
  )

  const setActiveSheet = (index: number) => {
    setImportState((prevState) => ({
      ...prevState,
      activeSheet: index,
    }))
  }

  const setTableNames = useCallback(
    (names: string[]) => {
      setImportState((prevState) => {
        const existingTableNames = tables.map((table) => table.name)

        // Process each name to ensure uniqueness
        const uniqueNames = names.map((name) => {
          let uniqueName = name
          let copyCount = 1

          while (existingTableNames.includes(uniqueName)) {
            uniqueName = `${name}${copyCount === 1 ? ' copy' : ` copy ${copyCount}`}`
            copyCount++
          }

          return uniqueName
        })

        return {
          ...prevState,
          tableNames: uniqueNames,
        }
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tables]
  )

  const updateTableName = useCallback(
    (index: number, name: string) => {
      setImportState((prevState) => {
        const existingTableNames = tables.map((table) => table.name)
        let uniqueName = name
        let copyCount = 1

        while (existingTableNames.includes(uniqueName)) {
          uniqueName = `${name}${copyCount === 1 ? ' copy' : ` copy ${copyCount}`}`
          copyCount++
        }

        const newItems = [...prevState.items]
        newItems[index].name = uniqueName

        return {
          ...prevState,
          items: newItems,
        }
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tables]
  )

  const updateItem = (index: number, item: ImportItem) => {
    setImportState((prevState) => {
      const newItems = [...prevState.items]
      newItems[index] = item
      return { ...prevState, items: newItems }
    })
  }

  return {
    importState,
    setImportState,
    startImport,
    nextStep,
    prevStep,
    resetImport,
    updateProcessingState,
    setActiveSheet,
    addImportItem,
    setTableNames,
    updateTableName,
    updateItem,
  }
}
